'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FieldPath, FieldValues } from 'react-hook-form';
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import { useTranslations } from 'next-intl';

import { cn } from '@/lib/utils';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import { Popover, PopoverTrigger } from '@/components/ui/popover';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import type {
  BaseFormFieldProps,
  FormOption,
  InfiniteSelectConfig
} from '@/types/base-form';

interface FormComboboxProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> extends BaseFormFieldProps<TFieldValues, TName> {
  /** Static options (for small/fixed lists) */
  options?: FormOption[];
  /** Infinite scroll config (from useInfiniteSelect hook) */
  infiniteConfig?: InfiniteSelectConfig;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  /** Display label for pre-selected value not yet in loaded pages */
  displayValue?: string;
}

function FormCombobox<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  control,
  name,
  label,
  description,
  required,
  options,
  infiniteConfig,
  placeholder,
  searchPlaceholder,
  emptyMessage,
  displayValue,
  disabled,
  className
}: FormComboboxProps<TFieldValues, TName>) {
  const tc = useTranslations('common');
  const [open, setOpen] = useState(false);

  const isInfiniteMode = !!infiniteConfig;
  const resolvedOptions = useMemo(
    () => infiniteConfig?.options ?? options ?? [],
    [infiniteConfig?.options, options]
  );

  // Scroll container ref — obtained via callback ref on the wrapping div
  const scrollRef = useRef<HTMLDivElement>(null);

  // Reset search when popover closes
  useEffect(() => {
    if (!open && infiniteConfig) {
      infiniteConfig.onSearchChange('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Guard against duplicate fetches — set synchronously before the async fetchNextPage resolves
  const fetchGuardRef = useRef(false);

  // Reset guard when fetching completes
  useEffect(() => {
    if (!infiniteConfig?.isFetchingNextPage) {
      fetchGuardRef.current = false;
    }
  }, [infiniteConfig?.isFetchingNextPage]);

  // Scroll-based infinite loading
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || !infiniteConfig || fetchGuardRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = el;
    if (
      scrollHeight - scrollTop - clientHeight < 80 &&
      infiniteConfig.hasNextPage
    ) {
      fetchGuardRef.current = true;
      infiniteConfig.fetchNextPage();
    }
  }, [infiniteConfig]);

  useEffect(() => {
    if (!open || !isInfiniteMode) return;

    const el = scrollRef.current;
    if (!el) return;

    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [open, isInfiniteMode, handleScroll]);

  const getSelectedLabel = useCallback(
    (value: string) => {
      if (displayValue) return displayValue;
      const match = resolvedOptions.find((o) => o.value === value);
      return match?.label;
    },
    [resolvedOptions, displayValue]
  );

  const getSelectedColor = useCallback(
    (value: string) => {
      const match = resolvedOptions.find((o) => o.value === value);
      return match?.color;
    },
    [resolvedOptions]
  );

  const getSelectedBadge = useCallback(
    (value: string) => {
      const match = resolvedOptions.find((o) => o.value === value);
      return match?.badge;
    },
    [resolvedOptions]
  );

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={className}>
          {label && (
            <FormLabel>
              {label}
              {required && <span className='ms-1 text-red-500'>*</span>}
            </FormLabel>
          )}
          <Popover open={open} onOpenChange={setOpen}>
            <FormControl>
              <PopoverTrigger asChild>
                <Button
                  type='button'
                  variant='outline'
                  role='combobox'
                  aria-expanded={open}
                  disabled={disabled}
                  className={cn(
                    'border-input h-9 w-full justify-between bg-transparent px-3 font-normal shadow-xs',
                    !field.value && 'text-muted-foreground'
                  )}
                >
                  <span className='flex min-w-0 items-center gap-2 truncate'>
                    {field.value && getSelectedColor(field.value) && (
                      <span
                        aria-hidden='true'
                        className='h-2.5 w-2.5 shrink-0 rounded-full ring-1 ring-black/10'
                        style={{
                          backgroundColor: getSelectedColor(field.value)
                        }}
                      />
                    )}
                    <span className='truncate'>
                      {field.value
                        ? (getSelectedLabel(field.value) ?? field.value)
                        : (placeholder ?? tc('search'))}
                    </span>
                    {field.value && getSelectedBadge(field.value) && (
                      <Badge variant='secondary' className='shrink-0'>
                        {getSelectedBadge(field.value)}
                      </Badge>
                    )}
                  </span>
                  <ChevronsUpDown className='text-muted-foreground ms-2 h-4 w-4 shrink-0 opacity-50' />
                </Button>
              </PopoverTrigger>
            </FormControl>
            <PopoverPrimitive.Portal>
              <PopoverPrimitive.Content
                align='start'
                sideOffset={4}
                className='bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-(--radix-popover-trigger-width) origin-(--radix-popover-content-transform-origin) rounded-md border p-0 shadow-md outline-hidden'
              >
                <Command shouldFilter={!isInfiniteMode}>
                  <CommandInput
                    placeholder={searchPlaceholder ?? tc('search')}
                    value={isInfiniteMode ? infiniteConfig.search : undefined}
                    onValueChange={
                      isInfiniteMode ? infiniteConfig.onSearchChange : undefined
                    }
                  />
                  {/* Own scroll container so we can reliably detect scroll position */}
                  <div
                    ref={scrollRef}
                    className='max-h-[300px] overflow-y-auto'
                    onScroll={isInfiniteMode ? handleScroll : undefined}
                  >
                    <CommandList className='max-h-none overflow-visible'>
                      {infiniteConfig?.isLoading ? (
                        <div className='flex items-center justify-center py-6'>
                          <Loader2 className='text-muted-foreground h-4 w-4 animate-spin' />
                        </div>
                      ) : (
                        <>
                          <CommandEmpty>
                            {emptyMessage ?? tc('noResults')}
                          </CommandEmpty>
                          <CommandGroup>
                            {resolvedOptions.map((option) => (
                              <CommandItem
                                key={option.value}
                                value={option.value}
                                disabled={option.disabled}
                                onSelect={(val) => {
                                  field.onChange(
                                    val === field.value ? '' : val
                                  );
                                  setOpen(false);
                                }}
                              >
                                {option.color && (
                                  <span
                                    aria-hidden='true'
                                    className='me-2 h-2.5 w-2.5 shrink-0 rounded-full ring-1 ring-black/10'
                                    style={{ backgroundColor: option.color }}
                                  />
                                )}
                                <span className='flex-1 truncate'>
                                  {option.label}
                                </span>
                                {option.badge && (
                                  <Badge
                                    variant='secondary'
                                    className='shrink-0'
                                  >
                                    {option.badge}
                                  </Badge>
                                )}
                                <Check
                                  className={cn(
                                    'ms-auto h-4 w-4 shrink-0',
                                    field.value === option.value
                                      ? 'opacity-100'
                                      : 'opacity-0'
                                  )}
                                />
                              </CommandItem>
                            ))}
                          </CommandGroup>
                          {isInfiniteMode &&
                            infiniteConfig.isFetchingNextPage && (
                              <div className='flex items-center justify-center py-2'>
                                <Loader2 className='text-muted-foreground h-4 w-4 animate-spin' />
                              </div>
                            )}
                        </>
                      )}
                    </CommandList>
                  </div>
                </Command>
              </PopoverPrimitive.Content>
            </PopoverPrimitive.Portal>
          </Popover>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

export { FormCombobox };
